import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const SHORT_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

export default function MonthPicker({ value, onChange, max }) {
  // value / max : 'YYYY-MM'
  const [y, m] = value.split('-').map(Number);
  const [open, setOpen]       = useState(false);
  const [gridYear, setGridYear] = useState(y);
  const ref = useRef(null);

  const [maxY, maxM] = max ? max.split('-').map(Number) : [9999, 12];

  // Fermer si clic extérieur
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sync gridYear quand value change depuis l'extérieur
  useEffect(() => { setGridYear(y); }, [y]);

  const goArrow = (delta) => {
    let nm = m + delta, ny = y;
    if (nm < 1)  { nm = 12; ny -= 1; }
    if (nm > 12) { nm = 1;  ny += 1; }
    const next = `${ny}-${String(nm).padStart(2, '0')}`;
    if (!max || next <= max) onChange(next);
  };

  const selectMonth = (mi) => {
    const next = `${gridYear}-${String(mi).padStart(2, '0')}`;
    if (!max || next <= max) { onChange(next); setOpen(false); }
  };

  const isDisabled = (mi) => max && `${gridYear}-${String(mi).padStart(2, '0')}` > max;
  const isSelected = (mi) => gridYear === y && mi === m;

  const atMax = max && value >= max;

  return (
    <div ref={ref} className="relative select-none">
      {/* Contrôle principal */}
      <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-1.5">
        <button
          onClick={() => goArrow(-1)}
          className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors text-slate-500 hover:text-slate-700"
        >
          <ChevronLeft size={15} />
        </button>

        {/* Label cliquable → ouvre la grille */}
        <button
          onClick={() => setOpen(v => !v)}
          className="flex items-center gap-1 px-2 py-0.5 rounded-lg hover:bg-slate-200 transition-colors"
        >
          <span className="text-sm font-bold text-slate-700 min-w-[120px] text-center">
            {MONTHS_FR[m - 1]} {y}
          </span>
          <ChevronDown size={13} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        <button
          onClick={() => goArrow(1)}
          disabled={!!atMax}
          className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors text-slate-500 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight size={15} />
        </button>
      </div>

      {/* Grille dropdown */}
      {open && (
        <div className="absolute z-50 mt-2 right-0 sm:left-0 bg-white border border-slate-200 rounded-2xl shadow-xl p-4 w-72">
          {/* Sélecteur d'année */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setGridYear(v => v - 1)}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-500"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-black text-slate-800">{gridYear}</span>
            <button
              onClick={() => setGridYear(v => Math.min(v + 1, maxY))}
              disabled={gridYear >= maxY}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Grille 12 mois */}
          <div className="grid grid-cols-4 gap-1.5">
            {SHORT_FR.map((label, i) => {
              const mi = i + 1;
              const disabled = isDisabled(mi);
              const selected = isSelected(mi);
              return (
                <button
                  key={mi}
                  onClick={() => !disabled && selectMonth(mi)}
                  disabled={disabled}
                  className={`py-2 rounded-xl text-xs font-bold transition-all ${
                    selected
                      ? 'bg-blue-600 text-white shadow-sm shadow-blue-200'
                      : disabled
                      ? 'text-slate-300 cursor-not-allowed'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
